import type { Definition } from '@exodus/dependency-types';
import type { Logger } from '@exodus/logger';
import type { CredentialIdAtom } from '@passkeys/application';
import type MpcClient from '@passkeys/mpc-client';
import { RPC } from '@passkeys/rpc';

import { EMBEDDABLE_SIGNER } from '@/ui/constants';
import { RETRY_OPEN_MESSAGE } from '../constants.js';
import createTransport from './transport.js';
import { createModalRpc } from './utils.js';

declare global {
  interface Window {
    nativeBridge?: {
      closeSigner: () => void;
      openSigner: (url: string) => void;
    };
    connect: (params?: object) => Promise<object | null>;
    signTransaction: (params?: object) => Promise<object | null>;
    signMessage: (params?: object) => Promise<object | null>;

    SKIP_SAFARI_INCOGNITO_CHECK?: boolean;
  }
}

type Dependencies = {
  credentialIdAtom: CredentialIdAtom;
  mpcClient: MpcClient;
  logger: Logger;
};

const createRPC = ({ credentialIdAtom, mpcClient, logger }: Dependencies) => {
  const searchParams = new URLSearchParams(window.location.search);
  const relayEnabled = searchParams.has('relay');

  if (relayEnabled) {
    logger.info('Signer configured to work in relay mode');
  }

  const callMethod = async ({ method, params }) => {
    const modalRpc = createModalRpc();
    const transport = await createTransport({
      modalRpc,
      mpcClient,
      relay: {
        enabled: relayEnabled,
        returnTo: searchParams.get('returnTo'),
      },
    });

    if (!transport) {
      return;
    }

    const rpc: any = new RPC({
      transport,
      allowedOrigins: [__PASSKEYS_NETWORK_URL__],
    });

    const credentialId = await credentialIdAtom.get();
    let args;
    if (__GLOBAL_SIGNER_METHODS__) {
      args = { credentialId, ...params };
    } else {
      args = { ...params, credentialId };
    }

    // When message goes through dapp, we need to attach the target so `@passkeys/core` can open the popup.
    if (!__STANDALONE_MODE__ && !EMBEDDABLE_SIGNER) {
      args.target = __PASSKEYS_NETWORK_URL__;
    }

    if (__STANDALONE_MODE__) {
      // In standalone mode there's no `@passkeys.core` to provide metadata.
      // Since the signer handles standalone mode uniquely, providing an empty
      // object as metadata is sufficient.
      // See: https://github.com/ExodusMovement/passkeys-signer/blob/f63bf4829e9243a04b97f28039df4100dfe81c89/apps/signer/src/ui/exodus.ts#L64-L66.
      args.metadata = {};
    }

    // First attempt to open send request
    let response = await rpc.callMethod(method, args);

    // If popup is blocked, prompt user to retry
    if (response?.error === 'popup blocked') {
      const retryOpen = await modalRpc.callMethod(
        'confirm',
        RETRY_OPEN_MESSAGE,
      );

      if (retryOpen) {
        response = await rpc.callMethod(method, args);
      }
    }

    // Close popup after request has been resolved.
    if (__STANDALONE_MODE__ && !EMBEDDABLE_SIGNER) {
      transport.close();
    }

    return response;
  };

  const connect = async (params = {}) => {
    return callMethod({ method: 'connect', params });
  };

  const signTransaction = async (params = {}) => {
    return callMethod({ method: 'signTransaction', params });
  };

  const signTransactions = async (params = {}) => {
    return callMethod({ method: 'signTransactions', params });
  };

  const signMessage = async (params = {}) => {
    return callMethod({ method: 'signMessage', params });
  };

  if (__GLOBAL_SIGNER_METHODS__) {
    window.connect = connect;
    window.signTransaction = signTransaction;
    window.signMessage = signMessage;
  }

  const exportPrivateKey = async (params = {}) => {
    return callMethod({ method: 'exportPrivateKey', params });
  };

  const shareWallet = async (params = {}) => {
    return callMethod({ method: 'shareWallet', params });
  };

  const setTheme = async (params = {}) => {
    return callMethod({ method: 'setTheme', params });
  };

  const shareActivity = (shareActivity: boolean) => {
    return callMethod({
      method: 'shareActivity',
      params: { shareActivity },
    });
  };

  return {
    connect,
    signTransaction,
    signTransactions,
    shareWallet,
    signMessage,
    exportPrivateKey,
    ...(EMBEDDABLE_SIGNER && { setTheme, shareActivity }),
  };
};

const passkeyModuleDefinition = {
  id: 'passkey',
  type: 'module',
  factory: createRPC,
  dependencies: ['credentialIdAtom', 'mpcClient', 'logger'],
} as const satisfies Definition;

export default passkeyModuleDefinition;
